import { useState, useEffect } from "react";
import { useLanguage } from "@/hooks/useLanguage";

interface CountdownTimerProps {
  targetDate: string;
  compact?: boolean;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const CountdownTimer = ({ targetDate, compact = false }: CountdownTimerProps) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null);
  const lang = useLanguage();

  useEffect(() => {
    const calculate = () => {
      const diff = new Date(targetDate).getTime() - Date.now();
      if (diff <= 0) return null;
      return {
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      };
    };

    setTimeLeft(calculate());
    const interval = setInterval(() => {
      const tl = calculate();
      if (!tl) clearInterval(interval);
      setTimeLeft(tl);
    }, 1000);

    return () => clearInterval(interval);
  }, [targetDate]);

  if (!timeLeft) return null;

  const labels =
    lang === "zh-cn"
      ? { days: "天", hours: "小时", minutes: "分", seconds: "秒" }
      : lang === "es"
        ? { days: "Días", hours: "Horas", minutes: "Min", seconds: "Seg" }
        : { days: "Days", hours: "Hours", minutes: "Min", seconds: "Sec" };

  const units = [
    { label: labels.days, value: timeLeft.days },
    { label: labels.hours, value: timeLeft.hours },
    { label: labels.minutes, value: timeLeft.minutes },
    { label: labels.seconds, value: timeLeft.seconds },
  ];

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        {units.map((u, i) => (
          <div key={u.label} className="flex items-center gap-2">
            <div className="text-center">
              <span className="text-lg font-bold text-primary-foreground tabular-nums">
                {String(u.value).padStart(2, "0")}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-primary-foreground/50 ml-0.5">
                {u.label}
              </span>
            </div>
            {i < units.length - 1 && (
              <span className="text-primary-foreground/30 font-bold">:</span>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      {units.map((u) => (
        <div
          key={u.label}
          className="flex flex-col items-center bg-primary-foreground/10 backdrop-blur-sm rounded-xl px-4 py-3 min-w-[68px] border border-primary-foreground/10"
        >
          <span className="text-2xl sm:text-3xl font-bold text-primary-foreground tabular-nums leading-none">
            {String(u.value).padStart(2, "0")}
          </span>
          <span className="text-[10px] uppercase tracking-wider text-primary-foreground/60 mt-1.5">
            {u.label}
          </span>
        </div>
      ))}
    </div>
  );
};

export default CountdownTimer;
